import { useState } from "react";

import { MOBILE_SCREEN_SIZE } from "@brandclub/common-ui";
import { styled } from "@mui/material";

import { buildAddressString } from "@/components/Checkout/utils";
import StoreAddressAutocompleteInput, {
  AutocompleteAddress,
} from "../../../components/CheckoutAddressAutocompleteInput";
import { InputContainer } from "@/components/Checkout/components/InputContainer";
import { CheckoutInputV2 } from "@/components/Checkout/components/CheckoutInputV2";
import { ShippingAddress } from "@/components/Checkout/types";

const Container = styled("div")(({ theme }) => ({
  transition: "all 0.3s ease-in-out",

  display: "flex",
  flexDirection: "column",
  gap: 16,
  ".title": {
    fontSize: 20,
    fontWeight: 600,

    [`@media screen and (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
      fontSize: 14,
    },
  },
}));

interface MobileAddressFormProps {
  value: Partial<ShippingAddress>;
  onChange: (address: Partial<ShippingAddress>) => void;
  disabled?: boolean;
}

const MobileAddressForm = ({
  value,
  onChange,
  disabled = false,
}: MobileAddressFormProps) => {
  const [expanded, setExpanded] = useState(false);

  const fullAddressString = buildAddressString(value);

  const handleChange = (field: keyof ShippingAddress, newValue: string) => {
    onChange({
      ...value,
      [field]: newValue,
    });
  };

  return (
    <Container>
      <InputContainer>
        <CheckoutInputV2
          label={"First Name"}
          value={value.firstName || ""}
          onChange={(e) => handleChange("firstName", e.target.value)}
          disabled={disabled}
        />
        <CheckoutInputV2
          label={"Last Name"}
          value={value.lastName || ""}
          onChange={(e) => handleChange("lastName", e.target.value)}
          disabled={disabled}
        />
      </InputContainer>
      <InputContainer>
        <StoreAddressAutocompleteInput
          searchTerm={value.address1 || ""}
          selectAddressCallback={(address: AutocompleteAddress) => {
            onChange({
              ...value,
              address1: address?.line1 || "",
              city: address?.city || "",
              state: address?.state || "",
              zipCode: address?.zip || "",
            });
            setExpanded(true);
          }}
          renderInput={(param: any) => (
            <CheckoutInputV2
              placeholder="Begin typing to search"
              label={"Street address"}
              value={expanded ? value.address1 : fullAddressString}
              onChange={(e) => {
                return handleChange("address1", e.target.value);
              }}
              disabled={disabled}
              {...param}
              onFocus={() => {
                if (value.address1?.length) {
                  setExpanded(true);
                }
              }}
              InputProps={{
                ...param.InputProps,
              }}
              inputProps={{
                ...param.inputProps,
                value: expanded ? value.address1 : fullAddressString,
              }}
            />
          )}
        />
      </InputContainer>
      {expanded ? (
        <>
          <InputContainer>
            <CheckoutInputV2
              label={"Suite, Apartment, C/O"}
              value={value.address2 || ""}
              disabled={disabled}
              onChange={(e) => handleChange("address2", e.target.value)}
            />
            <CheckoutInputV2
              label={"City"}
              value={value.city || ""}
              disabled={disabled}
              onChange={(e) => handleChange("city", e.target.value)}
            />
          </InputContainer>
          <InputContainer>
            <CheckoutInputV2
              label={"State"}
              value={value.state || ""}
              disabled={disabled}
              onChange={(e) => handleChange("state", e.target.value)}
            />
            <CheckoutInputV2
              label={"Zip Code"}
              value={value.zipCode || ""}
              disabled={disabled}
              onChange={(e) => handleChange("zipCode", e.target.value)}
            />
          </InputContainer>
        </>
      ) : null}
    </Container>
  );
};

export default MobileAddressForm;
